/**
 * BarChart (Phase 3, §6.3/§6.4) — SPINE. Grouped vertical bars on a single EUR
 * y-axis with an optional line overlay on the SAME axis (never dual-axis — used
 * for the cashflow Einnahmen/Ausgaben bars + net line). Thin bars with 4px
 * rounded data-ends anchored to the zero baseline, de-DE ticks/tooltips, a
 * crosshair tooltip, and a legend + table mirror via ChartFrame.
 */
import { useState } from 'react';
import { money, moneyShort } from '../../../lib/format.ts';
import { Axis, niceTicks, scaleLinear, type AxisTick } from './Axis.tsx';
import { ChartFrame } from './ChartFrame.tsx';
import { Legend, type LegendItem } from './Legend.tsx';
import { Tooltip, type TooltipRow } from './Tooltip.tsx';

export interface BarSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

export interface BarLineOverlay {
  label: string;
  color: string;
  values: number[];
}

export interface BarChartProps {
  title: string;
  subtitle?: string;
  description?: string;
  /** X category labels, already de-DE formatted (aligned with every `values`). */
  categories: string[];
  series: BarSeries[];
  /** Optional line overlaid on the same EUR axis (e.g. cashflow net). */
  netLine?: BarLineOverlay;
  formatValue?: (n: number) => string;
  formatTick?: (n: number) => string;
  aspect?: number;
}

const M = { left: 56, right: 16, top: 12, bottom: 28 };

function barPath(x: number, w: number, valueY: number, baseY: number, radius: number): string {
  const up = valueY <= baseY;
  const top = Math.min(valueY, baseY);
  const bottom = Math.max(valueY, baseY);
  const r = Math.max(0, Math.min(radius, w / 2, bottom - top));
  if (up) {
    return `M${x},${bottom} L${x},${top + r} Q${x},${top} ${x + r},${top} L${x + w - r},${top} Q${x + w},${top} ${x + w},${top + r} L${x + w},${bottom} Z`;
  }
  return `M${x},${top} L${x},${bottom - r} Q${x},${bottom} ${x + r},${bottom} L${x + w - r},${bottom} Q${x + w},${bottom} ${x + w},${bottom - r} L${x + w},${top} Z`;
}

export function BarChart({
  title,
  subtitle,
  description,
  categories,
  series,
  netLine,
  formatValue = money,
  formatTick = moneyShort,
  aspect = 0.5,
}: BarChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const n = categories.length;

  const legendItems: LegendItem[] = [
    ...series.map((s) => ({ label: s.label, color: s.color, shape: 'square' as const })),
    ...(netLine ? [{ label: netLine.label, color: netLine.color, shape: 'line' as const }] : []),
  ];

  const columns = ['Zeitraum', ...series.map((s) => s.label), ...(netLine ? [netLine.label] : [])];
  const rows: (string | number)[][] = categories.map((cat, i) => [
    cat,
    ...series.map((s) => formatValue(s.values[i] ?? 0)),
    ...(netLine ? [formatValue(netLine.values[i] ?? 0)] : []),
  ]);

  const scrollMinWidth = n > 8 ? n * 56 + M.left + M.right : undefined;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      description={description}
      aspect={aspect}
      scrollMinWidth={scrollMinWidth}
      legend={<Legend items={legendItems} />}
      table={{ columns, rows, caption: title }}
    >
      {({ width, height }) => {
        const plotLeft = M.left;
        const plotRight = width - M.right;
        const plotTop = M.top;
        const plotBottom = height - M.bottom;
        const innerW = Math.max(0, plotRight - plotLeft);
        const innerH = Math.max(0, plotBottom - plotTop);

        const allValues = [
          0,
          ...series.flatMap((s) => s.values),
          ...(netLine ? netLine.values : []),
        ];
        const scale = niceTicks(Math.min(...allValues), Math.max(...allValues), 5);
        const yScale = scaleLinear(scale.min, scale.max, plotBottom, plotTop);
        const baselineY = yScale(0);

        const yTicks: AxisTick[] = scale.ticks.map((v) => ({
          pos: yScale(v),
          label: formatTick(v),
        }));

        const bandW = n > 0 ? innerW / n : innerW;
        const center = (i: number) => plotLeft + bandW * (i + 0.5);
        const groupW = bandW * 0.7;
        const k = Math.max(1, series.length);
        const barW = (groupW / k) * 0.86;

        const xTicks: AxisTick[] = categories.map((label, i) => ({ pos: center(i), label }));

        const netPoints = netLine
          ? netLine.values.map((v, i) => `${center(i)},${yScale(v)}`).join(' ')
          : '';

        return (
          <>
            <Axis
              left={plotLeft}
              top={plotTop}
              innerWidth={innerW}
              innerHeight={innerH}
              yTicks={yTicks}
              xTicks={xTicks}
              baselineY={baselineY}
            />

            {series.map((s, si) =>
              s.values.map((v, i) => {
                const gx = center(i) - groupW / 2 + (si + 0.5) * (groupW / k) - barW / 2;
                return (
                  <path
                    key={`${s.key}-${i}`}
                    d={barPath(gx, barW, yScale(v), baselineY, 4)}
                    fill={s.color}
                    opacity={hover === null || hover === i ? 1 : 0.4}
                  />
                );
              }),
            )}

            {netLine ? (
              <>
                <polyline
                  points={netPoints}
                  fill="none"
                  stroke={netLine.color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {netLine.values.map((v, i) => (
                  <circle key={`net-${i}`} cx={center(i)} cy={yScale(v)} r={2.5} fill={netLine.color} />
                ))}
                {n > 0 ? (
                  <text
                    x={center(n - 1)}
                    y={yScale(netLine.values[n - 1] ?? 0) - 8}
                    textAnchor="end"
                    fontSize={11}
                    fontWeight={600}
                    fill={netLine.color}
                  >
                    {formatValue(netLine.values[n - 1] ?? 0)}
                  </text>
                ) : null}
              </>
            ) : null}

            {/* Hover hit areas + crosshair */}
            <g onPointerLeave={() => setHover(null)}>
              {categories.map((_, i) => (
                <rect
                  key={`hit-${i}`}
                  x={plotLeft + bandW * i}
                  y={plotTop}
                  width={bandW}
                  height={innerH}
                  fill="transparent"
                  onPointerEnter={() => setHover(i)}
                />
              ))}
              {hover !== null ? (
                <line
                  x1={center(hover)}
                  x2={center(hover)}
                  y1={plotTop}
                  y2={plotBottom}
                  stroke="var(--viz-axis)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  pointerEvents="none"
                />
              ) : null}
            </g>

            {hover !== null ? (
              <Tooltip
                x={center(hover)}
                y={plotTop}
                title={categories[hover]}
                chartWidth={width}
                rows={[
                  ...series.map<TooltipRow>((s) => ({
                    label: s.label,
                    value: formatValue(s.values[hover] ?? 0),
                    color: s.color,
                  })),
                  ...(netLine
                    ? [
                        {
                          label: netLine.label,
                          value: formatValue(netLine.values[hover] ?? 0),
                          color: netLine.color,
                        } satisfies TooltipRow,
                      ]
                    : []),
                ]}
              />
            ) : null}
          </>
        );
      }}
    </ChartFrame>
  );
}
